import { Role } from 'src/api/models/Role';
import { useUser } from 'stores/user/useUser';
import { onMounted, watch } from 'vue';
import { useProfileDetail } from 'stores/profile/useProfileDetail';
import { Capacitor } from '@capacitor/core';
import { ApplicationTheme } from 'src/utils/types';

const ADMIN_BASE_URLS: Partial<Record<ApplicationTheme, string>> = {
  [ApplicationTheme.FYZION]: 'https://api.fyzion.cz',
  [ApplicationTheme.MIND]: 'https://api.mindapp.cz',
};
declare global {
  interface Window {
    $crisp:
      | {
          push: (...args: unknown[]) => void;
        }
      | undefined;
    CRISP_WEBSITE_ID: string;
  }
}

export const useCrisp = (): void => {
  const userStore = useUser();
  const companyStore = useProfileDetail();

  const initCrisp = (): void => {
    if (typeof window === 'undefined') return;

    if (Capacitor.isNativePlatform()) return;

    if (window.$crisp) return;

    window.$crisp = [];
    window.$crisp?.push(['safe', true]);
    window.CRISP_WEBSITE_ID = 'ba36a96d-7b3b-49e2-bb77-9a7ee6a0bf76';

    const script = document.createElement('script');
    script.src = 'https://client.crisp.chat/l.js';
    script.async = true;
    document.head.appendChild(script);
  };

  const updateCrispSession = (): void => {
    const user = userStore.user;
    const company = companyStore.company;
    const theme =
      (process.env.THEME as ApplicationTheme) ?? ApplicationTheme.FYZION;
    const adminBase =
      ADMIN_BASE_URLS[theme] ?? ADMIN_BASE_URLS[ApplicationTheme.FYZION];

    window?.$crisp?.push([
      'set',
      'user:nickname',
      [`${user.first_name + ' ' + user.last_name}`],
    ]);
    window?.$crisp?.push(['set', 'user:email', `${user.email}`]);

    if (user.phone) {
      window?.$crisp?.push(['set', 'user:phone', [`${user.phone}`]]);
    }
    window?.$crisp?.push(['set', 'user:company', [`${user.company_name}`]]);

    if (company.company_logo) {
      window?.$crisp?.push([
        'set',
        'user:avatar',
        [`${company.company_logo.thumbnail_300x300}`],
      ]);
    }

    window?.$crisp?.push(['set', 'session:segments', [[theme]]]);

    window?.$crisp?.push([
      'set',
      'session:data',
      [
        [
          [
            'admin_url',
            `${adminBase}/admin/companies/company/${user.company_id}/change/`,
          ],
          [
            'impersonate',
            `${adminBase}/admin/companies/company/impersonate/${user.company_id}/`,
          ],
          ['company_id', `${user.company_id}`],
          ['patient_count', `${user.patient_count}`],
          ['user_id', `${user.id}`],
          ['package', `${user.package}`],
          ['payment_type', `${user.payment_model}`],
        ],
      ],
    ]);
  };

  onMounted(() => {
    initCrisp();
    window.$crisp?.push(['do', 'chat:hide']);

    watch(
      () => ({
        user: userStore.user,
        company: companyStore.company,
      }),
      (current, previous) => {
        if (!window.$crisp) return;

        const hadUser = !!previous?.user && !!previous?.company;
        const hasUser = !!current?.user && !!current?.company;

        if (!hasUser && hadUser) {
          window.$crisp.push(['do', 'session:reset']);
          window.$crisp.push(['do', 'chat:hide']);
        } else if (hasUser) {
          const allowedRoles = [
            Role.COMPANY_ADMIN,
            Role.COMPANY_FIRST_ADMIN,
            Role.RECEPTIONIST,
          ];
          if (current.user && allowedRoles.includes(current.user.role)) {
            updateCrispSession();
            window.$crisp?.push(['do', 'chat:show']);
          }
        }
      },
      { immediate: true, deep: false }
    );
  });
};
